import React, { useState } from "react";

import { sanitizeInvestorWalletNote } from "../../utils/investorWalletNote";
import { walletTransactionTypeLabel } from "../../utils/walletTransactionLabels";
import { hasTransferDetails, transferDetailLines } from "../../utils/walletTransferDetails";

const bdt = new Intl.NumberFormat("en-BD", { style: "currency", currency: "BDT", maximumFractionDigits: 2 });

function formatAmount(row) {
  const n = Number(row?.amount);
  return Number.isFinite(n) ? bdt.format(Math.abs(n)) : String(row?.amount ?? "—");
}

function shortRef(reference) {
  const r = String(reference ?? "").trim();
  if (!r) return "";
  if (r.length <= 28) return r;
  return `${r.slice(0, 12)}…${r.slice(-8)}`;
}

export default function WalletActivityTransactionRow({ row }) {
  const [open, setOpen] = useState(false);
  const dir = String(row?.direction ?? "").toUpperCase();
  const isCredit = dir === "CREDIT";
  const isDebit = dir === "DEBIT";
  const label = walletTransactionTypeLabel(row?.type, row);
  const when = row?.created_at ? new Date(row.created_at).toLocaleString() : "";
  const note = sanitizeInvestorWalletNote(row?.note);
  const ref = String(row?.reference ?? "").trim();
  const transferLines = transferDetailLines(row);
  const showTransferPreview = hasTransferDetails(row) && !open;

  return (
    <article
      className={`wallet-activity-txn${isCredit ? " wallet-activity-txn--credit" : ""}${
        isDebit ? " wallet-activity-txn--debit" : ""
      }`}
    >
      <button
        type="button"
        className="wallet-activity-txn__toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <div className="wallet-activity-txn__body">
          <div className="wallet-activity-txn__type">
            <span className="wallet-activity-txn__label">{label}</span>
            {dir ? (
              <span
                className={`wallet-txn__dir wallet-txn__dir--${
                  isCredit ? "credit" : isDebit ? "debit" : "neutral"
                }`}
              >
                {dir}
              </span>
            ) : null}
          </div>
          {when ? (
            <time className="wallet-activity-txn__time" dateTime={row.created_at}>
              {when}
              {ref ? ` · Ref ${shortRef(ref)}` : ""}
            </time>
          ) : null}
          {showTransferPreview ? (
            <p className="wallet-activity-txn__transfer-preview">
              {transferLines.map((line) => (
                <span key={line.label}>
                  <strong>{line.label}:</strong> {line.value}
                </span>
              ))}
            </p>
          ) : null}
          {note && !open ? <p className="wallet-activity-txn__preview">{note}</p> : null}
        </div>
        <span className="wallet-activity-txn__amount" aria-label={`Amount ${formatAmount(row)}`}>
          {isCredit ? "+" : isDebit ? "−" : ""}
          {formatAmount(row)}
        </span>
        <span className="wallet-activity-txn__chevron" aria-hidden>
          {open ? "▴" : "▾"}
        </span>
      </button>
      {open ? (
        <dl className="wallet-activity-txn__detail">
          {transferLines.map((line) => (
            <div key={line.label}>
              <dt>{line.label}</dt>
              <dd>{line.value}</dd>
            </div>
          ))}
          <div>
            <dt>Balance before</dt>
            <dd>{row.balance_before != null ? bdt.format(Number(row.balance_before)) : "—"}</dd>
          </div>
          <div>
            <dt>Balance after</dt>
            <dd>{row.balance_after != null ? bdt.format(Number(row.balance_after)) : "—"}</dd>
          </div>
          <div>
            <dt>Reference</dt>
            <dd className="wallet-activity-txn__mono">{ref || "—"}</dd>
          </div>
          <div className="wallet-activity-txn__detail--wide">
            <dt>Note</dt>
            <dd>{note || "—"}</dd>
          </div>
        </dl>
      ) : null}
    </article>
  );
}
